import type { JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AsyncState } from '@/components/async';
import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useDashboardPropertyDetail } from '@/hooks/useDashboardProperties';
import { UnitEditForm } from '@/routes/dashboard/unitEdit/UnitEditForm';
import {
  formatFloorLevelLabel,
  formatPropertyAddress,
  formatUnitLabel,
} from '@/lib/utils/propertyFormatting';

export function DashboardUnitEdit(): JSX.Element {
  const user = useDashboardUser();
  const { propertyId, unitId } = useParams<{ propertyId: string; unitId: string }>();
  const navigate = useNavigate();
  const normalizedPropertyId = (propertyId ?? '').trim();
  const normalizedUnitId = (unitId ?? '').trim();

  const detailQuery = useDashboardPropertyDetail({
    user,
    propertyId: normalizedPropertyId.length > 0 ? normalizedPropertyId : null,
  });

  const unitDetailPath = `/dashboard/properties/${normalizedPropertyId}/units/${normalizedUnitId}`;
  const backToUnit = () => {
    navigate(unitDetailPath);
  };
  const backToProperty = () => {
    navigate(`/dashboard/properties/${normalizedPropertyId}`);
  };

  const skeleton = (
    <div className="bg-surface-muted h-28 animate-pulse rounded-3xl border border-surface-subtle" />
  );

  return (
    <section className="flex flex-col gap-xl py-8">
      <button
        type="button"
        onClick={backToUnit}
        className="min-h-11 w-fit rounded-full border border-brand-primary/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      >
        Back to unit
      </button>

      <AsyncState
        data={detailQuery.data ?? null}
        isLoading={detailQuery.isLoading || detailQuery.isFetching}
        error={detailQuery.error}
        skeleton={skeleton}
      >
        {(detail) => {
          const unit = detail.units?.find((entry) => entry.id === normalizedUnitId) ?? null;

          if (!unit) {
            return (
              <div className="rounded-3xl border border-surface-subtle bg-white p-6 text-sm text-text-primary/80 shadow-sm">
                <p className="text-base font-semibold text-text-primary">Unit not found</p>
                <p className="mt-2">
                  The requested unit could not be located for this property. Return to the property
                  and pick another record.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <PillButton onClick={backToProperty}>Back to property</PillButton>
                </div>
              </div>
            );
          }

          const propertyLabel = formatPropertyAddress(detail.property.address);
          const unitLabel = `${formatUnitLabel(unit)} • ${formatFloorLevelLabel(unit.floorLevel)}`;

          return (
            <>
              <header className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
                  Edit unit
                </p>
                <h1 className="text-3xl font-semibold text-text-primary">{unitLabel}</h1>
                <p className="text-sm text-text-primary/70">{propertyLabel}</p>
              </header>

              <div className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-sm">
                <UnitEditForm
                  propertyId={normalizedPropertyId}
                  unit={unit}
                  onCancel={backToUnit}
                  onSaved={backToUnit}
                  onDeleted={backToProperty}
                />
              </div>
            </>
          );
        }}
      </AsyncState>
    </section>
  );
}
